import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import ScienceIcon from '@mui/icons-material/Science';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { config } from '../config/env';

interface PricingPlansProps {
  open: boolean;
  onClose: () => void;
  currentPlan: 'Free' | 'Basic' | 'Pro' | 'Beta-Testing';
  userToken: string | null;
  onSubscriptionChange?: () => void;
  language?: string;
  hasSubscription?: boolean;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
}

// Übersetzungen
const translations: Record<string, Record<string, string>> = {
  'pricing.title': {
    de: 'Preisplan wählen',
    en: 'Choose a Plan',
    fr: 'Choisir un forfait',
    it: 'Scegli un piano',
  },
  'pricing.monthly': {
    de: '/Monat',
    en: '/month',
    fr: '/mois',
    it: '/mese',
  },
  'pricing.free': {
    de: 'Kostenlos',
    en: 'Free',
    fr: 'Gratuit',
    it: 'Gratuito',
  },
  'pricing.current': {
    de: 'Aktueller Plan',
    en: 'Current Plan',
    fr: 'Forfait actuel',
    it: 'Piano attuale',
  },
  'pricing.upgrade': {
    de: 'Upgrade',
    en: 'Upgrade',
    fr: 'Mettre à niveau',
    it: 'Aggiorna',
  },
  'pricing.manage': {
    de: 'Abo verwalten',
    en: 'Manage Subscription',
    fr: 'Gérer l\'abonnement',
    it: 'Gestisci abbonamento',
  },
  'pricing.users': {
    de: 'Benutzer',
    en: 'Users',
    fr: 'Utilisateurs',
    it: 'Utenti',
  },
  'pricing.cvs': {
    de: 'CV-Daten-Sets',
    en: 'CV Data Sets',
    fr: 'Jeux de données CV',
    it: 'Set di dati CV',
  },
  'pricing.cv': {
    de: 'CV-Daten-Set',
    en: 'CV Data Set',
    fr: 'Jeu de données CV',
    it: 'Set di dati CV',
  },
  'pricing.user': {
    de: 'Benutzer',
    en: 'User',
    fr: 'Utilisateur',
    it: 'Utente',
  },
  'pricing.pdf': {
    de: 'PDF-Downloads',
    en: 'PDF Downloads',
    fr: 'Téléchargements PDF',
    it: 'Download PDF',
  },
  'pricing.pdf.no': {
    de: 'Keine PDF-Downloads',
    en: 'No PDF Downloads',
    fr: 'Pas de téléchargements PDF',
    it: 'Nessun download PDF',
  },
  'pricing.pdf.yes': {
    de: 'PDF-Downloads inklusive',
    en: 'PDF Downloads included',
    fr: 'Téléchargements PDF inclus',
    it: 'Download PDF inclusi',
  },
  'pricing.pdf.limited': {
    de: 'PDFs pro Monat',
    en: 'PDFs per month',
    fr: 'PDF par mois',
    it: 'PDF al mese',
  },
  'pricing.pdf.total': {
    de: 'PDFs total',
    en: 'PDFs total',
    fr: 'PDF au total',
    it: 'PDF totali',
  },
  'pricing.error': {
    de: 'Fehler beim Erstellen der Zahlungssession. Bitte versuche es erneut.',
    en: 'Error creating payment session. Please try again.',
    fr: 'Erreur lors de la création de la session de paiement. Veuillez réessayer.',
    it: 'Errore nella creazione della sessione di pagamento. Riprova.',
  },
  'pricing.redirecting': {
    de: 'Weiterleitung zu Stripe...',
    en: 'Redirecting to Stripe...',
    fr: 'Redirection vers Stripe...',
    it: 'Reindirizzamento a Stripe...',
  },
  'pricing.unlimited': {
    de: 'Unbegrenzt',
    en: 'Unlimited',
    fr: 'Illimité',
    it: 'Illimitato',
  },
  'pricing.beta': {
    de: 'Beta-Testing',
    en: 'Beta Testing',
    fr: 'Beta Test',
    it: 'Beta Testing',
  },
  'pricing.sharelinks': {
    de: 'Share-Links',
    en: 'Share Links',
    fr: 'Liens de partage',
    it: 'Link di condivisione',
  },
  'pricing.sharelinks.no': {
    de: 'Keine Share-Links',
    en: 'No Share Links',
    fr: 'Pas de liens de partage',
    it: 'Nessun link di condivisione',
  },
  'pricing.downgrade': {
    de: 'Abo kündigen',
    en: 'Cancel Subscription',
    fr: 'Résilier l\'abonnement',
    it: 'Annulla abbonamento',
  },
  'pricing.included': {
    de: 'Inklusive',
    en: 'Included',
    fr: 'Inclus',
    it: 'Incluso',
  },
  'pricing.switch.free': {
    de: 'Zu Free wechseln',
    en: 'Switch to Free',
    fr: 'Passer à Free',
    it: 'Passa a Free',
  },
  'pricing.switch.beta': {
    de: 'Zu Beta wechseln',
    en: 'Switch to Beta',
    fr: 'Passer à Beta',
    it: 'Passa a Beta',
  },
  'pricing.cancel': {
    de: 'Abo kündigen',
    en: 'Cancel Subscription',
    fr: 'Résilier l\'abonnement',
    it: 'Annulla abbonamento',
  },
  'pricing.cancel.at': {
    de: 'Kündigt am',
    en: 'Cancels on',
    fr: 'Résiliation le',
    it: 'Annullamento il',
  },
  'pricing.until': {
    de: 'Laufzeit bis',
    en: 'Valid until',
    fr: 'Valable jusqu\'au',
    it: 'Valido fino al',
  },
  'pricing.cancelled': {
    de: 'Gekündigt',
    en: 'Cancelled',
    fr: 'Résilié',
    it: 'Annullato',
  },
  'pricing.cancel.revert': {
    de: 'Kündigung widerrufen',
    en: 'Revert Cancellation',
    fr: 'Annuler la résiliation',
    it: 'Annulla la cancellazione',
  },
};

const plans = [
  {
    type: 'Free' as const,
    name: 'Free',
    price: 0,
    icon: <StarIcon sx={{ fontSize: 32, color: '#94a3b8' }} />,
    color: '#94a3b8',
    gradient: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
    features: {
      maxUsers: 1,
      maxCvs: 1,
      pdfDownloads: 3,
      shareLinks: false,
    },
  },
  {
    type: 'Beta-Testing' as const,
    name: 'Beta-Testing',
    price: 0,
    icon: <ScienceIcon sx={{ fontSize: 32, color: '#10b981' }} />,
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    hideFeatures: true,
    features: {
      maxUsers: -1,
      maxCvs: -1,
      pdfDownloads: -1,
      shareLinks: true,
    },
  },
  {
    type: 'Basic' as const,
    name: 'Basic',
    price: 16.90,
    icon: <RocketLaunchIcon sx={{ fontSize: 32, color: '#667eea' }} />,
    color: '#667eea',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    features: {
      maxUsers: 1,
      maxCvs: 3,
      pdfDownloads: 50,
      shareLinks: false,
    },
    popular: true,
  },
  {
    type: 'Pro' as const,
    name: 'Pro',
    price: 149.90,
    icon: <WorkspacePremiumIcon sx={{ fontSize: 32, color: '#f59e0b' }} />,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
    features: {
      maxUsers: 5,
      maxCvs: 20,
      pdfDownloads: 200,
      shareLinks: true,
    },
  },
];

export default function PricingPlans({
  open,
  onClose,
  currentPlan,
  userToken,
  onSubscriptionChange,
  language = 'de',
  hasSubscription = false,
  cancelAtPeriodEnd = false,
  currentPeriodEnd = null,
}: PricingPlansProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [switchedToFree, setSwitchedToFree] = useState(false);
  const [switchedToBeta, setSwitchedToBeta] = useState(false);

  const t = (key: string) => {
    return translations[key]?.[language] || translations[key]?.['de'] || key;
  };

  const handleSubscribe = async (planType: string) => {
    if (!userToken || planType === 'Free' || planType === 'Beta-Testing') return;

    setLoading(planType);
    setError(null);

    try {
      const response = await fetch(`${config.backend.fullApiUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planType }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }

      const data = await response.json();

      if (data.url) {
        // Weiterleitung zu Stripe Checkout
        window.location.href = data.url;
      }
    } catch (err: any) {
      console.error('Fehler beim Erstellen der Checkout-Session:', err);
      setError(t('pricing.error'));
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!userToken) return;

    setLoading('manage');
    setError(null);

    try {
      const response = await fetch(`${config.backend.fullApiUrl}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      console.error('Fehler beim Öffnen der Abo-Verwaltung:', err);
      setError(err.message || t('pricing.error'));
      setLoading(null);
    }
  };

  const handleSwitchToFree = async () => {
    if (!userToken) return;

    setLoading('free-switch');
    setError(null);

    try {
      const response = await fetch(`${config.backend.fullApiUrl}/api/stripe/switch-to-free`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }

      onClose();
      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
    } catch (err: any) {
      console.error('Fehler beim Wechsel auf Free:', err);
      setError(err.message || t('pricing.error'));
    } finally {
      setLoading(null);
    }
  };

  const handleSwitchToBeta = async () => {
    if (!userToken) return;

    setLoading('beta-switch');
    setError(null);

    try {
      const response = await fetch(`${config.backend.fullApiUrl}/api/stripe/switch-to-beta`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }

      onClose();
      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
    } catch (err: any) {
      console.error('Fehler beim Wechsel auf Beta-Testing:', err);
      setError(err.message || t('pricing.error'));
    } finally {
      setLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!userToken) return;

    setLoading('cancel');
    setError(null);

    try {
      const response = await fetch(`${config.backend.fullApiUrl}/api/stripe/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }

      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
    } catch (err: any) {
      console.error('Fehler beim Kündigen:', err);
      setError(err.message || t('pricing.error'));
    } finally {
      setLoading(null);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!userToken) return;

    setLoading('reactivate');
    setError(null);

    try {
      const response = await fetch(`${config.backend.fullApiUrl}/api/stripe/reactivate-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Unbekannter Fehler');
      }

      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
    } catch (err: any) {
      console.error('Fehler beim Widerrufen der Kündigung:', err);
      setError(err.message || t('pricing.error'));
    } finally {
      setLoading(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'en' ? 'en-GB' : language === 'fr' ? 'fr-FR' : language === 'it' ? 'it-IT' : 'de-CH');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 1800 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2.5,
        }}
      >
        <Typography component="span" variant="h5" sx={{ fontWeight: 700 }}>
          {t('pricing.title')}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
        {/* Abstand zwischen Header und Inhalt */}
        <Box sx={{ height: 16 }} />
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Haupt-Pläne (Free, Basic, Pro) */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
          }}
        >
          {plans
            .filter((plan) => plan.type !== 'Beta-Testing')
            .map((plan) => {
            const isCurrent = currentPlan === plan.type;
            const isUpgrade = !isCurrent && plan.type !== 'Free' &&
              (currentPlan === 'Free' || currentPlan === 'Beta-Testing' || (currentPlan === 'Basic' && plan.type === 'Pro'));

            return (
              <Box
                key={plan.type}
                sx={{
                  position: 'relative',
                  bgcolor: 'white',
                  borderRadius: 3,
                  border: isCurrent ? `2px solid ${plan.color}` : '1px solid #e2e8f0',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  transform: plan.popular ? 'scale(1.02)' : 'none',
                  boxShadow: isCurrent
                    ? `0 4px 20px ${plan.color}30`
                    : plan.popular
                    ? '0 4px 16px rgba(0,0,0,0.1)'
                    : '0 1px 4px rgba(0,0,0,0.05)',
                  '&:hover': {
                    boxShadow: `0 6px 24px ${plan.color}25`,
                    transform: plan.popular ? 'scale(1.03)' : 'scale(1.01)',
                  },
                }}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: -28,
                      background: plan.gradient,
                      color: 'white',
                      px: 4,
                      py: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      transform: 'rotate(45deg)',
                      transformOrigin: 'center',
                      zIndex: 1,
                    }}
                  >
                    POPULAR
                  </Box>
                )}

                {/* Header */}
                <Box
                  sx={{
                    p: 3,
                    pb: 2,
                    textAlign: 'center',
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>{plan.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                    {plan.name}
                  </Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: plan.color }}>
                      {plan.price === 0 ? t('pricing.free') : `CHF ${plan.price.toFixed(2)}`}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', visibility: plan.price === 0 ? 'hidden' : 'visible' }}>
                      {t('pricing.monthly')}
                    </Typography>
                  </Box>
                </Box>

                {/* Features */}
                <Box sx={{ px: 3, pb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: plan.color }} />
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {plan.features.maxUsers === -1 ? t('pricing.unlimited') : plan.features.maxUsers} {plan.features.maxUsers === 1 ? t('pricing.user') : t('pricing.users')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: plan.color }} />
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {plan.features.maxCvs === -1 ? t('pricing.unlimited') : plan.features.maxCvs} {plan.features.maxCvs === 1 ? t('pricing.cv') : t('pricing.cvs')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 18,
                          color: plan.features.pdfDownloads !== 0 ? plan.color : '#cbd5e1',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: plan.features.pdfDownloads !== 0 ? '#475569' : '#94a3b8',
                          textDecoration: plan.features.pdfDownloads !== 0 ? 'none' : 'line-through',
                        }}
                      >
                        {plan.features.pdfDownloads === 0
                          ? t('pricing.pdf.no')
                          : plan.features.pdfDownloads === -1
                            ? t('pricing.pdf.yes')
                            : `${plan.features.pdfDownloads} ${t(plan.type === 'Free' ? 'pricing.pdf.total' : 'pricing.pdf.limited')}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon
                        sx={{
                          fontSize: 18,
                          color: plan.features.shareLinks ? plan.color : '#cbd5e1',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: plan.features.shareLinks ? '#475569' : '#94a3b8',
                          textDecoration: plan.features.shareLinks ? 'none' : 'line-through',
                        }}
                      >
                        {plan.features.shareLinks ? t('pricing.sharelinks') : t('pricing.sharelinks.no')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Action Button */}
                <Box sx={{ p: 3, pt: 1 }}>
                  {isCurrent ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Chip
                        label={cancelAtPeriodEnd && plan.type !== 'Free' 
                          ? t('pricing.current') 
                          : t('pricing.current')}
                        sx={{
                          bgcolor: `${plan.color}15`,
                          color: plan.color,
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          py: 2.5,
                        }}
                      />
                      {hasSubscription && plan.type !== 'Free' && currentPeriodEnd && (
                        <Typography variant="body2" sx={{ 
                          textAlign: 'center', 
                          color: '#64748b', 
                          fontSize: '0.8rem',
                        }}>
                          {`${t('pricing.until')} ${formatDate(currentPeriodEnd)}`}
                        </Typography>
                      )}
                      {cancelAtPeriodEnd && plan.type !== 'Free' && (
                        <Typography variant="body2" sx={{ 
                          textAlign: 'center', 
                          color: '#ef4444', 
                          fontSize: '0.8rem',
                          fontWeight: 600,
                        }}>
                          {t('pricing.cancelled')}
                        </Typography>
                      )}
                      {hasSubscription && plan.type !== 'Free' && !cancelAtPeriodEnd && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleCancelSubscription}
                          disabled={loading === 'cancel'}
                          sx={{
                            color: '#ef4444',
                            fontSize: '0.8rem',
                            textTransform: 'none',
                          }}
                        >
                          {loading === 'cancel' ? <CircularProgress size={14} /> : t('pricing.cancel')}
                        </Button>
                      )}
                      {hasSubscription && plan.type !== 'Free' && cancelAtPeriodEnd && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleReactivateSubscription}
                          disabled={loading === 'reactivate'}
                          sx={{
                            color: '#10b981',
                            fontSize: '0.8rem',
                            textTransform: 'none',
                            fontWeight: 600,
                          }}
                        >
                          {loading === 'reactivate' ? <CircularProgress size={14} /> : t('pricing.cancel.revert')}
                        </Button>
                      )}
                    </Box>
                  ) : isUpgrade ? (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleSubscribe(plan.type)}
                      disabled={loading !== null}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        background: plan.gradient,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        '&:hover': {
                          background: plan.gradient,
                          filter: 'brightness(0.95)',
                        },
                      }}
                    >
                      {loading === plan.type ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                          {t('pricing.redirecting')}
                        </Box>
                      ) : (
                        `${t('pricing.upgrade')} → ${plan.name}`
                      )}
                    </Button>
                  ) : !isCurrent && plan.type === 'Free' && currentPlan === 'Beta-Testing' ? (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSwitchToFree}
                      disabled={loading !== null || switchedToFree}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        background: switchedToFree ? '#10b981' : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
                          filter: 'brightness(0.95)',
                        },
                      }}
                    >
                      {loading === 'free-switch' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                        </Box>
                      ) : switchedToFree ? (
                        '✓'
                      ) : (
                        t('pricing.switch.free')
                      )}
                    </Button>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Beta-Testing - kompakter Streifen unter den Plänen */}
        {currentPlan === 'Beta-Testing' && (
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'white',
              border: currentPlan === 'Beta-Testing' ? '2px solid #10b981' : '1px solid #e2e8f0',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              boxShadow: currentPlan === 'Beta-Testing' ? '0 2px 8px rgba(16, 185, 129, 0.15)' : '0 1px 4px rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ScienceIcon sx={{ fontSize: 24, color: '#10b981' }} />
              <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                Beta-Testing
              </Typography>
            </Box>
            {currentPlan === 'Beta-Testing' ? (
              <Chip
                label={t('pricing.current')}
                sx={{
                  bgcolor: '#10b98115',
                  color: '#10b981',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              />
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={handleSwitchToBeta}
                disabled={loading !== null || switchedToBeta}
                sx={{
                  borderRadius: 2,
                  background: switchedToBeta ? '#10b981' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  px: 3,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  },
                }}
              >
                {loading === 'beta-switch' ? (
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                ) : switchedToBeta ? (
                  '✓'
                ) : (
                  t('pricing.switch.beta')
                )}
              </Button>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
